// Rôles de la collectivité : rôles de référence et rôles locaux composés à partir d'eux.
import { PERMISSIONS } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Formulaire, GrilleChamps, Panneau, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { requete } from '../../api/client';
import { useRoles } from '../../api/requetes';
import type { Role } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { LIBELLES_DOMAINES, LIBELLES_PERMISSIONS, peut } from '../../session/permissions';
import commun from '../../commun/commun.module.css';
import styles from './administration.module.css';

function Permissions({ permissions }: { permissions: Record<string, string[]> }) {
  return (
    <ul className={styles.listePermissions}>
      {Object.entries(permissions).map(([domaine, liste]) => (
        <li key={domaine}>
          <strong>{LIBELLES_DOMAINES[domaine] ?? domaine}</strong> : {liste.map((p) => LIBELLES_PERMISSIONS[p] ?? p).join(', ')}
        </li>
      ))}
    </ul>
  );
}

function FormulaireRole({ roles, onTermine }: { roles: Role[]; onTermine: () => void }) {
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [choix, setChoix] = useState<Record<string, string[]>>({});
  // Seules les permissions présentes dans un rôle de référence sont proposées (règle de l'API).
  const autorisees = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const r of roles.filter((x) => x.reference)) {
      for (const [d, ps] of Object.entries(r.permissions)) {
        const s = m.get(d) ?? new Set<string>();
        for (const p of ps) s.add(p);
        m.set(d, s);
      }
    }
    return [...m.entries()].sort(([a], [b]) => (LIBELLES_DOMAINES[a] ?? a).localeCompare(LIBELLES_DOMAINES[b] ?? b, 'fr'));
  }, [roles]);

  const creation = useAction({
    executer: () =>
      requete<Role>('/roles', {
        methode: 'POST',
        corps: { code, libelle, permissions: Object.fromEntries(Object.entries(choix).filter(([, v]) => v.length > 0)) },
      }),
    succes: (r) => `Rôle local ${r.libelle} créé${r.sensible ? ' (sensible : son attribution à soi-même demandera une validation)' : ''}.`,
    invalider: [['roles']],
    apres: onTermine,
  });

  const basculer = (domaine: string, p: string) =>
    setChoix((c) => {
      const actuelles = c[domaine] ?? [];
      return { ...c, [domaine]: actuelles.includes(p) ? actuelles.filter((x) => x !== p) : [...actuelles, p] };
    });

  const refusees = (creation.erreur as { details?: { permissions_refusees?: string[] } } | null)?.details?.permissions_refusees;

  return (
    <Formulaire
      libelle="Nouveau rôle local"
      onSoumettre={() => creation.lancer(undefined)}
      erreurs={creation.erreursChamps.map((e) => ({ champ: e.champ === 'code' ? 'role-code' : e.champ === 'libelle' ? 'role-libelle' : 'role-permissions', message: e.message }))}
      actions={
        <>
          <Bouton type="submit" chargement={creation.enCours}>
            Créer le rôle
          </Bouton>
          <Bouton variante="secondaire" onClick={onTermine}>
            Annuler
          </Bouton>
        </>
      }
    >
      {creation.messageErreur && creation.erreursChamps.length === 0 ? (
        <Bandeau type="erreur">
          {creation.messageErreur}
          {refusees ? ` : ${refusees.join(', ')}` : ''}
        </Bandeau>
      ) : null}
      <GrilleChamps>
        <Champ id="role-code" libelle="Code" aide="Minuscules, chiffres et soulignés, par exemple agent_accueil" value={code} onChange={(e) => setCode(e.target.value)} required />
        <Champ id="role-libelle" libelle="Libellé" value={libelle} onChange={(e) => setLibelle(e.target.value)} required />
      </GrilleChamps>
      <fieldset id="role-permissions" className={styles.matrice}>
        <legend>Permissions</legend>
        <p className={commun.description}>
          Seules les permissions existant dans un rôle de référence peuvent être combinées. Le caractère sensible est déduit des domaines choisis.
        </p>
        <div className={styles.defilement} tabIndex={0} role="region" aria-label="Matrice des permissions">
          <table>
            <thead>
              <tr>
                <th scope="col">Domaine</th>
                {PERMISSIONS.map((p) => (
                  <th key={p} scope="col">
                    {LIBELLES_PERMISSIONS[p]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {autorisees.map(([domaine, ps]) => (
                <tr key={domaine}>
                  <th scope="row">{LIBELLES_DOMAINES[domaine] ?? domaine}</th>
                  {PERMISSIONS.map((p) => (
                    <td key={p}>
                      {ps.has(p) ? (
                        <input
                          type="checkbox"
                          checked={choix[domaine]?.includes(p) ?? false}
                          onChange={() => basculer(domaine, p)}
                          aria-label={`${LIBELLES_DOMAINES[domaine] ?? domaine} : ${LIBELLES_PERMISSIONS[p]}`}
                        />
                      ) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>
    </Formulaire>
  );
}

export function PageRoles() {
  const { contexte } = useSession();
  const roles = useRoles();
  const [creation, setCreation] = useState(false);
  const colonnes: ColonneTableau<Role>[] = [
    { id: 'libelle', entete: 'Rôle', cellule: (r) => r.libelle, valeurTri: (r) => r.libelle, enTeteLigne: true, masquable: false },
    { id: 'code', entete: 'Code', cellule: (r) => r.code, valeurTri: (r) => r.code },
    { id: 'origine', entete: 'Origine', cellule: (r) => (r.reference ? 'Référence nationale' : 'Rôle local'), valeurTri: (r) => (r.reference ? 0 : 1) },
    { id: 'sensible', entete: 'Sensible', cellule: (r) => (r.sensible ? <PuceEtat ton="attente" libelle="Sensible" /> : 'Non'), valeurTri: (r) => (r.sensible ? 1 : 0) },
    { id: 'permissions', entete: 'Permissions', cellule: (r) => <Permissions permissions={r.permissions} /> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Rôles"
        description="Un rôle sensible attribué par une personne à elle-même attend la validation d'une autre personne (règle des quatre yeux)."
        actions={
          peut(contexte, 'utilisateurs', 'administrer') && !creation ? (
            <Bouton icone={Plus} onClick={() => setCreation(true)}>
              Nouveau rôle local
            </Bouton>
          ) : null
        }
      />
      <EtatRequete chargement={roles.isPending} erreur={roles.error} onReessayer={() => void roles.refetch()} fonction="La liste des rôles">
        {creation ? (
          <Panneau titre="Nouveau rôle local">
            <FormulaireRole roles={roles.data ?? []} onTermine={() => setCreation(false)} />
          </Panneau>
        ) : null}
        <Tableau legende="Rôles de la collectivité" colonnes={colonnes} lignes={roles.data ?? []} cleLigne={(r) => r.id} cleColonnes="roles" />
      </EtatRequete>
    </>
  );
}
